import { fromEvent, EMPTY, debounceTime, map, BehaviorSubject, merge } from 'rxjs';
import { tap, distinctUntilChanged } from 'rxjs/operators';

/**
 * Class representing the search bar component.
 * Manages the search input, reset button, and related events.
 */
export default class SearchBarComponent {
  constructor() {
    this.DEBOUNCE_DELAY_MS = 300;
    this.subscriptions = [];
    this.currentSearchText = '';
    this.inputChange$ = EMPTY;
    this.inputFocus$ = EMPTY;
    this.inputBlur$ = EMPTY;
  }

  /**
   * Initializes the search bar component with the provided configuration.
   * @param {Object} config - The configuration object.
   * @param {HTMLElement} config.searchInput - The search input element.
   * @param {HTMLElement} config.resetButton - The reset button element.
   * @param {HTMLElement} config.searchBarHighlight - The highlight element for the search bar.
   * @param {string} config.lastSearchText - The last search text to populate the input with.
   */
  init({ searchInput = null, resetButton = null, searchBarHighlight = null, lastSearchText = '' } = {}) {
    this.searchInput = searchInput;
    this.resetButton = resetButton;
    this.searchBarHighlight = searchBarHighlight;
    this.currentSearchText = lastSearchText;

    this.searchInputChange$ = new BehaviorSubject(this.currentSearchText);
    this.initializeUIState();
  }

  /**
   * Applies initial side effects such as binding events and setting initial states.
   */
  initializeUIState() {
    this.bindEvents();
    this.updateResetButtonVisibility();

    const hasSearchText = SearchBarComponent.hasSearchText(this.currentSearchText);
    if (hasSearchText) {
      this.setLastSearchText(this.currentSearchText);
    }
  }

  /**
   * Binds events to the component by setting up event listeners and initial states.
   */
  bindEvents() {
    this.registerInputEvents();
    this.registerResetButtonEvent();
    this.registerInputFocusEvent();
    this.registerInputBlurEvent();
  }

  /**
   * Sets the last search text in the input field.
   * @param {string} text - The text to set in the search input.
   */
  setLastSearchText(text) {
    this.searchInput.value = text;
    this.toggleInputHighlight();
  }

  /**
   * Toggles the highlight state of the search bar based on input focus and text presence.
   * @param {boolean} highlight - Whether to highlight the search bar.
   */
  toggleInputHighlight(highlight = false) {
    if (this.searchBarHighlight) {
      const isActive = this.isSearchBarActive(highlight, this.currentSearchText);
      this.searchBarHighlight.classList.toggle('nhs-n1-c-search-bar--active', isActive);
    }
  }

  /**
   * Determines if the search bar is active based on highlight state and search text.
   * @param {boolean} highlight - Whether to highlight the search bar.
   * @param {string} searchText - The current search text.
   * @returns {boolean} True if the search bar is active, false otherwise.
   */
  isSearchBarActive(highlight = false, searchText = '') {
    if (this.searchBarHighlight) {
      return highlight || SearchBarComponent.hasSearchText(searchText);
    }
    return false;
  }

  /**
   * Registers input events for the search input field.
   */
  registerInputEvents() {
    if (this.searchInput) {
      const domInputChange$ = fromEvent(this.searchInput, 'input').pipe(
        map(event => event.target.value?.trim() ?? '')
      );

      this.inputChange$ = merge(domInputChange$, this.searchInputChange$).pipe(
        debounceTime(this.DEBOUNCE_DELAY_MS),
        distinctUntilChanged(),
        tap(text => this.handleInputChange(text))
      );

      const subscription = this.inputChange$.subscribe();
      this.subscriptions.push(subscription);
    }
  }

  /**
   * Handles search input changes.
   */
  handleInputChange(text) {
    this.currentSearchText = text;
    this.updateResetButtonVisibility();
    this.toggleInputHighlight();
  }

  /**
   * Registers focus event for the search input field.
   */
  registerInputFocusEvent() {
    if (this.searchInput) {
      this.inputFocus$ = fromEvent(this.searchInput, 'focus');

      const inputFocusSubscription = this.inputFocus$
        .pipe(
          tap(() => this.handleInputFocus(true))
        )
        .subscribe();

      this.subscriptions.push(inputFocusSubscription);
    }
  }

  /**
   * Registers blur event for the search input field.
   */
  registerInputBlurEvent() {
    if (this.searchInput) {
      this.inputBlur$ = fromEvent(this.searchInput, 'blur');

      const inputBlurSubscription = this.inputBlur$
        .pipe(tap(() => this.handleInputFocus(false)))
        .subscribe();

      this.subscriptions.push(inputBlurSubscription);
    }
  }

  /**
   * Handles the input blur event to update UI state.
   */
  handleInputFocus(isFocused) {
    this.updateResetButtonVisibility();
    this.toggleInputHighlight(isFocused);
  }

  /**
   * Registers click event for the reset button.
   */
  registerResetButtonEvent() {
    if (this.resetButton) {
      const resetButtonClick$ = fromEvent(this.resetButton, 'click');
      const resetButtonClickSubscription = resetButtonClick$
        .pipe(
          tap(() => this.handleResetButtonClick())
        )
        .subscribe();
      this.subscriptions.push(resetButtonClickSubscription);
    }
  }

  /**
   * Handles the reset button click event to clear the search input.
   */
  handleResetButtonClick() {
    if (this.searchInput) {
      this.currentSearchText = '';
      this.searchInput.value = '';

      this.updateResetButtonVisibility();
      this.toggleInputHighlight();
      this.searchInputChange$.next('');
    }
  }

  /**
   * Checks if there is any text in the search input.
   * @returns {boolean} True if there is text, false otherwise.
   */
  static hasSearchText(text) {
    return (text?.trim().length ?? 0) > 0;
  }

  /**
   * Determines whether to show the reset button based on the search text.
   * @param {string} searchText - The current search text.
   * @returns {boolean} True if the reset button should be shown, false otherwise.
   */
  static shouldShowResetButton(searchText) {
    return SearchBarComponent.hasSearchText(searchText);
  }

  /**
   * Updates the visibility of the reset button based on the search input state.
   */
  updateResetButtonVisibility() {
    if (this.resetButton) {
      this.resetButton.hidden = !SearchBarComponent.hasSearchText(this.currentSearchText);
    }
  }

  /**
   * Gets the input change event observer.
   * @returns {Observable} The input change event observer.
   */
  getInputChangeObserver() {
    return this.inputChange$ ?? EMPTY;
  }

  /**
   * Gets the input focus event observer.
   * @returns {Observable} The input focus event observer.
   */
  getInputFocusObserver() {
    return this.inputFocus$ ?? EMPTY;
  }

  /**
   * Gets the input blur event observer.
   * @returns {Observable} The input blur event observer.
   */
  getInputBlurObserver() {
    return this.inputBlur$ ?? EMPTY;
  }

  /** Cleans up subscriptions to prevent memory leaks. */
  destroy() {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => subscription.unsubscribe());
      this.subscriptions = [];
    }
  }
}